import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TranslationFile } from 'i18n'
import { useLocation } from 'react-router'
import queryString from 'query-string'

import './SignPsd2.scss'
import { ApplicationDispatchContext } from 'contexts/ApplicationContext'
import { MSP_ACTIONS, MspDispatchContext } from 'components/multistep/Multistep'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import BankSelector from 'components/goCardless/BankSelector'
import { IGoCardlessBankItem } from 'interfaces/i-gocardless'
import goCardlessServices from 'services/goCardlessServices'
import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'

export const SignPsd2De = () => {
	const { t } = useTranslation([
		TranslationFile.Translation,
		TranslationFile.Forms,
		TranslationFile.Dropdown,
	])
	const [items, setItems] = useState<IGoCardlessBankItem[]>([])
	const location = useLocation()
	const { loanApplication }: IApplicationContext = useContext(
		ApplicationDispatchContext,
	)
	const { msp, dispatchMsp } = useContext(MspDispatchContext)

	const [selectedItem, setSelectedItem] = useState<
		IGoCardlessBankItem | undefined
	>(undefined)

	const isPsd2Completed = () => {
		return (
			(queryString.parse(location.search.toLowerCase()) ?? '')
				.psd2status === 'ok' &&
			!(queryString.parse(location.search.toLowerCase()) ?? '').error
		)
	}

	const getPsd2SignErrorMessage = (errorCode: string | string[]) => {
		switch (errorCode) {
			case 'UserSessionRejected':
				return 'GoCardlessUserSessionRejected'
			case 'UserCancelledSession':
				return 'GoCardlessUserCancelledSession'
			case 'RequisitionLinkReused':
				return 'GoCardlessRequisitionLinkReused'
			case 'InstitutionTechnicalError':
			case 'UnknownError':
				return 'GoCardlessUnknownError'
			case 'GoCardlessTechnicalError':
			case 'CompanyRequisitionLimitReached':
				return 'GoCardlessTechnicalError'
			default:
				return 'GoCardlessTechnicalError'
		}
	}

	const [completing, setCompleting] = useState<boolean>(() =>
		isPsd2Completed(),
	)
	const [completeError, setCompleteError] = useState<boolean>(false)
	const [completeErrorCode, setCompleteErrorCode] = useState<string>('')
	const [psd2SignError, setPsd2SignError] = useState<string | string[]>('')

	const completeGoCardless = async () => {
		setCompleting(true)
		setCompleteError(false)
		setCompleteErrorCode('')
		try {
			const queryParams = new URLSearchParams(window.location.search)
			const response = await goCardlessServices.complete({
				applicationId: loanApplication?.applicationData?.loanNumber!,
				id: queryParams.get('ref')!,
			})
			if (response.success) {
				next()
			} else {
				setCompleteErrorCode(response.errorCode)
				setCompleteError(true)
			}
		} catch (error) {
			setCompleteError(true)
		} finally {
			setCompleting(false)
		}
	}

	useEffect(() => {
		const fetchBanks = async () => {
			try {
				const response =
					await goCardlessServices.getGoCardlessInstitutions('de')
				setItems(response)
			} catch (error) {
				console.error('Error fetching banks:', error)
			}
		}

		if (!isPsd2Completed()) {
			fetchBanks()
		} else {
			completeGoCardless()
		}
	}, [])

	useEffect(() => {
		const errorCode = (queryString.parse(location.search) ?? '').error
		setPsd2SignError(errorCode ?? '')
	}, [location.search])

	const next = () => {
		dispatchMsp({
			type: MSP_ACTIONS.SET_INDEX,
			payload: {
				index: msp.index + 1,
			},
		})
	}

	return (
		<div className="sign-psd2">
			{completing ? (
				<CircleSpinner />
			) : completeError ? (
				['TooManyRequests', 'Conflict'].includes(completeErrorCode) ? (
					<ErrorBox message={t('PSD2UpdateFailedNoRetry')} />
				) : (
					<ErrorBox
						message={t('PSD2UpdateFailed')}
						button={
							<button
								id="psd2-retry-button"
								className="btn btn-primary"
								onClick={completeGoCardless}>
								{t('TryAgain')}
							</button>
						}
					/>
				)
			) : (
				<>
					<div className="sign-psd2-section">
						{psd2SignError ? (
							<ErrorBox
								message={t(
									getPsd2SignErrorMessage(psd2SignError),
								)}
							/>
						) : (
							<></>
						)}
						<span>{t('ConfirmIncome')}</span>
						<BankSelector
							items={items}
							selectedItem={selectedItem}
							onSelect={setSelectedItem}></BankSelector>
						<br />
						<span>{t('Psd2Description')}</span>
						<div className="sign-psd2-button-container">
							<button
								id="psd2-sign-button"
								disabled={!selectedItem}
								className="btn btn-primary"
								type="button"
								key="sign-psd2-button"
								onClick={(e) => {
									e.preventDefault()
									setTimeout(async () => {
										const goCardlessLink =
											await goCardlessServices.createGoCardlessLink(
												{
													redirect:
														window.location.origin +
														'/application?psd2Status=ok',
													institutionId:
														selectedItem?.id!,
													userLanguage: 'de',
													transactionTotalDays:
														selectedItem?.transactionTotalDays!,
													maxAccessValidForDays:
														selectedItem?.maxAccessValidForDays!,
												},
											)
										window.location.href =
											goCardlessLink.link
									}, 400)
								}}>
								{t('SIGNPSD2')}
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	)
}
